import { FunctionComponent, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import ModalFooter from "../../../../shared/modalFooter"
import { useMutation } from "@apollo/client"
import { SUBSCRIPTION_ADD } from "../../../../../graphql/mutations/subscription_add"
import MutationErrorBanner from "../../../../shared/graphQlResponse"
import ManageSubscriptionForm from "../form"
import { getSubscriptionAddMutationVariables } from "../manageSubscriptionFormUtil/api"
import { MultiValue, SingleValue } from "react-select"
import { LabelValueOptionType } from "../../../../../types/commonTypes"
import { GET_SUBSCRIPTIONS_FOR_USER } from "../../../../../graphql/queries/subscriptionsForUser"
import { ReactMultiSelectOptionT } from "../../../../shared/input/react-select/multiSelect"
import ManageSubscriptionsActions from "../../manageSubscriptions/actions"
import {
    ManageSubscriptionFormI,
    ManageSubscriptionSubcriberOptionsT,
    ManageSubscriptionViewerActionT,
} from "../manageSubscriptionFormUtil/types"
import { navigateAfterMutation } from "../../../navigate"
import { GET_SUBSCRIPTIONS_FOR_ORG } from "../../../../../graphql/queries/subscriptionsForOrg"
import { useNotificationsContext } from "../../../context/notificationsContext"

interface AddSubscriptionFormComponentI {
    ruleOptions: LabelValueOptionType[]
    subscriberOptions: ManageSubscriptionSubcriberOptionsT[]
    viewerAction: ManageSubscriptionViewerActionT
}

const AddSubscriptionForm: FunctionComponent<AddSubscriptionFormComponentI> = ({
    ruleOptions,
    viewerAction,
    subscriberOptions,
}) => {
    const navigate = useNavigate()
    const notificationsContext = useNotificationsContext()
    const { pathname } = useLocation()
    const [addSubscriptionMutation, { loading: mutationLoading }] =
        useMutation(SUBSCRIPTION_ADD)

    // On the add subscription form, we can just use the first subscriber option as the initial subscriber
    const initialSubscriber = subscriberOptions[0]
    const initialSubscriptionWells: ReactMultiSelectOptionT[] =
        initialSubscriber.subscriberWellPermissions.map((well) => {
            return {
                label: well.label,
                value: well.value,
                isSelected: false,
                isDisabled: false,
            }
        })

    const [addSubscriptionForm, setAddSubscriptionForm] =
        useState<ManageSubscriptionFormI>({
            subscriberId: initialSubscriber.subscriberId,
            subscriptionWells: initialSubscriptionWells,
            rule: {
                label: ruleOptions[0].label,
                value: ruleOptions[0].value,
            },
            includeNewWells: false,
            enableEmail: false,
            enableText: false,
            byPassDnd: false,
            initialSilenceUnixMs: 0,
            silenceForm: {
                timeValue: 0,
                timeUnit: "s",
            },
        })

    const [mutationErrorBanner, setMutationErrorBanner] =
        useState<boolean>(false)

    // handlers
    const handleRuleNameChange = (e: SingleValue<LabelValueOptionType>) => {
        if (e?.value && e.label) {
            setAddSubscriptionForm({
                ...addSubscriptionForm,
                rule: {
                    label: e.label,
                    value: e.value,
                },
            })
        }
    }

    // When the subscriber is changed, we need to reset the form state to the new subscriber
    const handleSubscriberChange = (e: SingleValue<LabelValueOptionType>) => {
        if (e?.value && e.label) {
            // find the selected subscriber from the subscriber options and set the form state to the new subscriber
            const selectedSubscriber = subscriberOptions.find(
                (sub) => sub.subscriberId === e.value
            )
            if (!selectedSubscriber) {
                return
            }

            setAddSubscriptionForm({
                ...addSubscriptionForm,
                subscriptionWells:
                    selectedSubscriber.subscriberWellPermissions.map((well) => {
                        return {
                            label: well.label,
                            value: well.value,
                            isSelected: false,
                            isDisabled: false,
                        }
                    }),
                subscriberId: selectedSubscriber.subscriberId,
                byPassDnd: false,
                enableEmail: false,
                enableText: false,
                includeNewWells: false,
                initialSilenceUnixMs: 0,
                silenceForm: {
                    timeValue: 0,
                    timeUnit: "s",
                },
            })
        }
    }

    // newValue is an array of the wells that are selected in the multi select component
    const handleSelectedWellsChange = (
        newValue: MultiValue<ReactMultiSelectOptionT>
    ) => {
        const selectedWellValues = new Set(
            newValue.map((option) => option.value)
        )
        const updatedWellOptions = addSubscriptionForm.subscriptionWells.map(
            (well) => ({
                ...well,
                isSelected: selectedWellValues.has(well.value),
            })
        )

        setAddSubscriptionForm({
            ...addSubscriptionForm,
            subscriptionWells: updatedWellOptions,
        })
    }

    const handleUpdateSubscriptionPreferences = (
        inputId: keyof ManageSubscriptionFormI,
        value: boolean
    ) => {
        setAddSubscriptionForm({
            ...addSubscriptionForm,
            [inputId]: value,
        })
    }

    const handleSelectAllWells = (updatedSelect: boolean) => {
        setAddSubscriptionForm({
            ...addSubscriptionForm,
            subscriptionWells: addSubscriptionForm.subscriptionWells.map(
                (well) => {
                    return { ...well, isSelected: updatedSelect }
                }
            ),
        })
    }

    // submit handlers
    const handleSubmitAddSubscription = () => {
        const mutationVariables =
            getSubscriptionAddMutationVariables(addSubscriptionForm)

        addSubscriptionMutation({
            variables: {
                AlertSubscriptionsAddInput: { inputs: [mutationVariables] },
            },
            refetchQueries: [
                GET_SUBSCRIPTIONS_FOR_USER,
                {
                    query: GET_SUBSCRIPTIONS_FOR_ORG,
                    variables: {
                        input: {
                            orgIDs: [
                                notificationsContext.selectedOrganization.value,
                            ],
                        },
                    },
                },
            ],
        })
            .then(() => {
                navigate(navTo.to, navTo.options)
            })
            .catch((error) => {
                console.error("Add subscription mutation error:", error)
                setMutationErrorBanner(true)
            })
    }

    // constants for both the form and the actions
    const formType = viewerAction === "admin" ? "AddAdmin" : "AddSelf"
    const navTo = navigateAfterMutation(pathname)

    // constants for ManageSubscriptionForm
    const selectedSubscriber = subscriberOptions.find(
        (sub) => sub.subscriberId === addSubscriptionForm.subscriberId
    ) as ManageSubscriptionSubcriberOptionsT

    // get LabelValueOptionType for the subscriber options to be used in the multi select component
    const formSubscriberOptions: LabelValueOptionType[] = subscriberOptions.map(
        (sub) => {
            return {
                label: sub.name,
                value: sub.subscriberId,
            }
        }
    )

    // constants for ManageSubscriptionsActions
    const selectedSubscribedWells =
        addSubscriptionForm.subscriptionWells.filter((wl) => {
            return wl.isSelected
        })

    return (
        <>
            <ManageSubscriptionForm
                handleRuleNameChange={handleRuleNameChange}
                handleSelectedWellsChange={handleSelectedWellsChange}
                handleUpdateSubscriptionPreferences={
                    handleUpdateSubscriptionPreferences
                }
                handleSubscriberChange={handleSubscriberChange}
                handleSelectAllWells={handleSelectAllWells}
                formType={formType}
                form={addSubscriptionForm}
                ruleOptions={ruleOptions}
                subscriberOptions={formSubscriberOptions}
                selectedSubscriber={selectedSubscriber}
            />
            {!mutationErrorBanner && (
                <ManageSubscriptionsActions
                    alertRuleId={addSubscriptionForm.rule.value}
                    formType={formType}
                    selectedSubscribedWells={selectedSubscribedWells}
                />
            )}
            {mutationErrorBanner && (
                <>
                    <MutationErrorBanner
                        message={
                            <div>
                                There was problem adding your subscription.
                                Click{" "}
                                <u
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                        navigate(navTo.to, navTo.options)
                                    }
                                >
                                    here
                                </u>{" "}
                                or close the modal to be redirected.
                            </div>
                        }
                    />
                </>
            )}
            <ModalFooter
                advanceText="Submit"
                disableAdvance={mutationLoading || mutationErrorBanner}
                handleAdvanceClick={handleSubmitAddSubscription}
                disablePrevious={mutationLoading}
                handlePreviousClick={() => navigate(-1)}
                previousButtonCondition="default"
                previousText="Cancel"
            />
        </>
    )
}

export default AddSubscriptionForm
